import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { useStore } from '../context/StoreContext';

const Hero = () => {
  const { ads, user, liveSettings } = useStore();

  if (!ads.heroAdEnabled) return null;

  const handleSubscribe = () => {
    window.open('https://pages.razorpay.com/rcktalgonewplan', '_blank');
  };

  return (
    // Added min-h-[100vh] for mobile to ensure content isn't squashed vertically on small screens
    <div className="relative w-full min-h-[100vh] lg:min-h-[650px] lg:h-[85vh] flex items-center justify-center overflow-hidden bg-dark-netflixDark">
      
      {/* Cinematic Background - Spline 3D Embed */}
      <div className="absolute inset-0 z-0">
        <iframe 
          src='https://my.spline.design/techinspired3dassetsgrowchart-NFqKELbMrBJ56AQWDuv3bYLg/' 
          frameBorder='0' 
          width='100%' 
          height='100%'
          className="absolute inset-0 w-full h-full opacity-80 pointer-events-none"
        ></iframe>
        
        {/* Dark Overlays for Text Readability (Netflix Style) */}
        <div className="absolute inset-0 bg-gradient-to-r from-dark-netflixDark via-dark-netflixDark/70 to-transparent w-full md:w-[70%] pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-t from-dark-bg via-transparent to-dark-bg/40 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-dark-bg to-transparent pointer-events-none" />
        
        {/* Subtle animated floating chart lines/particles simulation */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-rocket/10 rounded-full blur-[100px] animate-float opacity-50 pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-[120px] animate-pulse-slow opacity-30 pointer-events-none" />
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 md:px-12 relative z-10 w-full mt-20 pb-16 lg:pb-0">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left Column: Text & CTAs */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="col-span-1 lg:col-span-7 order-2 lg:order-1"
          >
            <h1 className="text-[40px] sm:text-[50px] md:text-[64px] lg:text-[72px] font-black text-white leading-[1.05] mb-6 tracking-tight drop-shadow-2xl">
              Master the Markets <br className="hidden sm:block" /> with <span className="text-transparent bg-clip-text bg-gradient-to-r from-rocket via-[#ff8833] to-[#ffaa66]">Advanced Algorithms</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-300 mb-10 max-w-2xl font-medium drop-shadow-md">
              {ads.subheadline || 'Join 10,000+ traders using AI-powered strategies to beat the market.'}
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-5">
              {!user.isSubscribed ? (
                <button 
                  onClick={handleSubscribe}
                  className="w-full sm:w-auto bg-rocket hover:bg-rocket-hover text-white px-8 py-4 rounded font-bold text-lg transition-all shadow-[0_0_20px_rgba(255,102,0,0.4)] hover:shadow-[0_0_35px_rgba(255,102,0,0.6)] transform hover:scale-105 flex items-center justify-center"
                >
                  {ads.ctaText}
                  <SafeIcon name="ChevronRight" className="w-5 h-5 ml-2" />
                </button>
              ) : (
                <button className="w-full sm:w-auto bg-white text-black hover:bg-gray-200 px-8 py-4 rounded font-bold text-lg transition-all transform hover:scale-105 flex items-center justify-center">
                  <SafeIcon name="Play" className="w-5 h-5 mr-2 fill-current" />
                  Resume Learning
                </button>
              )}
              <button className="w-full sm:w-auto glass hover:bg-white/10 text-white px-8 py-4 rounded font-bold text-lg transition-all flex items-center justify-center group">
                <SafeIcon name="PlayCircle" className="w-6 h-6 mr-2 text-gray-300 group-hover:text-white transition-colors" />
                Watch Demo
              </button>
            </div>
          </motion.div>

          {/* Right Column: Live Stream Glass Card */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.9, rotateX: 10 }}
            animate={{ opacity: 1, scale: 1, rotateX: 0 }}
            transition={{ duration: 1, delay: 0.2, ease: "easeOut" }}
            className="col-span-1 lg:col-span-5 order-1 lg:order-2 w-full max-w-lg mx-auto lg:max-w-none perspective-1000"
          >
            <Link to="/live" className={`block relative group w-full aspect-video rounded-xl overflow-hidden glass shadow-2xl transform transition-all duration-500 hover:scale-[1.03] hover:-translate-y-2 ${liveSettings.isLive ? 'border-rocket/50 shadow-[0_0_40px_rgba(255,102,0,0.2)] hover:shadow-[0_0_60px_rgba(255,102,0,0.4)]' : 'border-white/10 hover:border-white/30'}`}>
              <div className="absolute inset-0 bg-dark-card">
                <img src="https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?auto=format&fit=crop&q=80&w=800" alt="Live Stream Preview" className="w-full h-full object-cover opacity-50 group-hover:opacity-70 transition-opacity duration-700 mix-blend-overlay" />
              </div>
              <div className="absolute inset-0 flex flex-col justify-between p-5 bg-gradient-to-t from-black via-transparent to-black/60">
                {/* Top Bar */}
                <div className="flex justify-between items-start">
                  {liveSettings.isLive ? (
                    <div className="bg-danger text-white text-[11px] font-bold px-3 py-1.5 rounded flex items-center space-x-2 shadow-[0_0_15px_rgba(229,9,20,0.6)] backdrop-blur-md">
                      <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                      <span className="tracking-wider">LIVE NOW</span>
                    </div>
                  ) : (
                    <div className="glass text-white text-[11px] font-bold px-3 py-1.5 rounded flex items-center tracking-wider">
                      OFFLINE
                    </div>
                  )}
                  
                  {/* Viewer Count */}
                  <motion.div 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1, duration: 1 }}
                    className="glass px-3 py-1.5 rounded text-xs text-white flex items-center font-medium"
                  >
                    <SafeIcon name="Users" className="w-3.5 h-3.5 mr-1.5 text-rocket" />
                    1,248
                  </motion.div>
                </div>

                {/* Center Play Button */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-16 h-16 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center border border-white/20 transform group-hover:scale-110 group-hover:bg-rocket/90 group-hover:border-rocket transition-all duration-300">
                    <SafeIcon name="Play" className="w-6 h-6 text-white ml-1 fill-current" />
                  </div>
                </div>

                {/* Bottom Info */}
                <div className="mt-auto transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                  <h3 className="text-white font-bold text-lg leading-tight drop-shadow-md">
                    {liveSettings.title}
                  </h3>
                  <p className="text-gray-300 text-sm mt-1 drop-shadow-md line-clamp-1">
                    {liveSettings.isLive ? liveSettings.description : `Next stream: ${new Date(liveSettings.nextLiveAt).toLocaleDateString()}`}
                  </p>
                </div>
              </div>
            </Link>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Hero;